import { IconButton, PullOutNavMenu } from '@greystar/designsystem';
import { getWindowLocation, useAnalytics } from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { MenuItem, NavMenuFields } from '@greystar/ui';
import {
  Link as JssLink,
  LinkFieldValue,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import { useEffect, useState } from 'react';

const NavMenu = (props: NavMenuFields): JSX.Element => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({});
  const [activeMenuTitle, setActiveMenuTitle] = useState<string>('');
  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const location = property?.location ?? {};
  const addressParts = [
    location?.address,
    location?.city,
    location?.state,
    location?.postalCode,
  ].filter((part) => part?.trim());
  const property_location = addressParts.length > 0 ? addressParts.join(', ') : '';

  useEffect(() => {
    const results = props.fields?.data?.datasource?.children?.results;
    if (Array.isArray(results)) {
      setMenuItems(results);
    }
  }, [props.fields?.data?.datasource]);

  const trackBurgerIconClick = () => {
    const eventData = {
      event: 'header_interaction',
      action_type: 'burger icon click',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: 'header',
      module_name: 'navigation bar',
      inpage_location: 'top',
      click_text: 'Menu',
      click_type: 'internal',
      destination_url: getWindowLocation(),
    };
    analytics.trackEvent('header_interaction', eventData);
  };

  const trackMenuClose = () => {
    const eventData = {
      event: 'primary_navigation',
      action_type: 'cancel',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: '',
      module_name: 'navigation bar',
      inpage_location: 'top',
      click_text: activeMenuTitle ?? 'Menu',
      click_type: 'internal',
      destination_url: getWindowLocation(),
    };

    analytics.trackEvent('primary_navigation', eventData);
  };

  const trackLinkClick = (
    linkField: LinkFieldValue,
    clickLink: string,
    inpageLocation: string,
    parentTitle: string | undefined
  ) => {
    const eventData = {
      event: clickLink == 'sub_link' ? 'secondary_navigation' : 'primary_navigation',
      action_type: 'click',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: clickLink === 'sub_link' ? parentTitle ?? '' : '',
      module_name: 'navigation bar',
      inpage_location: inpageLocation,
      click_text: linkField?.text ?? 'Untitled',
      click_type: 'internal',
      destination_url: linkField?.href ?? '',
    };
    analytics.trackEvent(
      clickLink == 'sub_link' ? 'secondary_navigation' : 'primary_navigation',
      eventData
    );
  };

  const trackAccordionToggle = (item: MenuItem, isOpen: boolean, inpageLocation: string) => {
    const linkField = item?.link?.value?.value as LinkFieldValue;
    if (isOpen) {
      setActiveMenuTitle(linkField?.text ?? 'Untitled');
    }

    const eventData = {
      event: 'primary_navigation',
      action_type: isOpen ? 'expand' : 'collapse',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: '',
      module_name: 'navigation bar',
      inpage_location: inpageLocation,
      click_text: linkField?.text ?? 'Untitled',
      click_type: 'internal',
      destination_url: linkField?.href ?? '',
    };

    analytics.trackEvent('primary_navigation', eventData);
  };

  const toggleAccordion = (item: MenuItem) => {
    const isCurrentlyOpen = openAccordions[item.id] ?? false;
    trackAccordionToggle(item, !isCurrentlyOpen, 'top');
    setOpenAccordions((prev) => ({
      ...prev,
      [item.id]: !prev[item.id],
    }));
  };

  const handleMenuToggle = () => {
    setIsOpen(true);
    trackBurgerIconClick();
  };

  const handleMenuClose = () => {
    trackMenuClose();
    setActiveMenuTitle('');
    setIsOpen(false);
  };

  const renderMenuItems = (items: MenuItem[], depth = 0, parentTitle?: string): JSX.Element[] => {
    return items.map((item) => {
      const linkField = item?.link?.value?.value as LinkFieldValue;
      const title = linkField?.text ?? 'Untitled';
      const children = item?.children?.results ?? [];
      const isAccordionOpen = openAccordions[item.id] ?? false;

      if (children.length > 0) {
        return (
          <PullOutNavMenu.Accordion
            key={item.id}
            isOpen={isAccordionOpen}
            menuTitle={title}
            buttonProps={{
              onClick: () => toggleAccordion(item),
            }}
          >
            {renderMenuItems(children, depth + 1, title)}
          </PullOutNavMenu.Accordion>
        );
      }

      return (
        <PullOutNavMenu.Link variant={depth > 0 ? 'subNav' : undefined} key={item.id}>
          <JssLink
            field={linkField}
            onClick={() =>
              trackLinkClick(linkField, depth > 0 ? 'sub_link' : 'main_link', 'top', parentTitle)
            }
            data-testid={`nav-link-${title.toLowerCase()}`}
          >
            <span>{title}</span>
          </JssLink>
        </PullOutNavMenu.Link>
      );
    });
  };

  return (
    <div className="burger-nav-menu">
      <IconButton
        data-testid="menu-button"
        onClick={handleMenuToggle}
        variant="floatingPrimary"
        iconName="Menu"
        size="medium"
        aria-label={t('menu_toggle_label') || 'Toggle Menu'}
      />

      {isOpen && (
        <PullOutNavMenu onClose={handleMenuClose}>
          <PullOutNavMenu.Content hasBorderBottom={false}>
            {menuItems.length > 0 ? (
              renderMenuItems(menuItems)
            ) : (
              <p>{t('loading_menu') || 'Loading...'}</p>
            )}
          </PullOutNavMenu.Content>
        </PullOutNavMenu>
      )}
    </div>
  );
};

export default NavMenu;
